import { dbTrue, type ZohoBankAccountDTO } from "../data/zohoRecordsApi";

// Reading Zoho's Banking screen the way a CA taking over the books would.

export type BankGroup = "bank" | "credit_card" | "cash" | "other";

export const BANK_GROUP_ORDER: BankGroup[] = ["bank", "credit_card", "cash", "other"];

export const BANK_GROUP_LABEL: Record<BankGroup, string> = {
  bank: "Bank accounts",
  credit_card: "Credit cards",
  cash: "Cash",
  other: "Other",
};

/**
 * Zoho files everything on its Banking screen under one of three types, and
 * anything it adds later lands in "Other" rather than silently reading as a
 * bank account.
 */
export function bankGroupForType(type: string | null): BankGroup {
  if (type === "bank" || type === "credit_card" || type === "cash") return type;
  return "other";
}

/**
 * ZOUR-120. Petty cash is a Zoho bank account with no number, marked by the CA.
 * Only a bank-type row counts, so a flag left on a row Zoho has since retyped
 * reads as off.
 */
export function isPettyCash(account: ZohoBankAccountDTO): boolean {
  return account.account_type === "bank" && dbTrue(account.is_petty_cash ?? null);
}

/**
 * Offered only on a bank account with no number in Zoho: Zoho cash accounts
 * take no statements, and an account with a number is a real bank account.
 */
export function canBePettyCash(account: ZohoBankAccountDTO): boolean {
  return account.account_type === "bank" && (account.masked_number ?? "").trim() === "";
}

/** Petty cash sits under Cash, the way the tenant groups it. */
export function bankGroupForAccount(account: ZohoBankAccountDTO): BankGroup {
  return isPettyCash(account) ? "cash" : bankGroupForType(account.account_type);
}

export function groupBankAccounts(
  accounts: ZohoBankAccountDTO[]
): { group: BankGroup; accounts: ZohoBankAccountDTO[] }[] {
  return BANK_GROUP_ORDER.map((group) => ({
    group,
    accounts: accounts.filter((account) => bankGroupForAccount(account) === group),
  })).filter((section) => section.accounts.length > 0);
}

/**
 * A balance, grouped the way the currency is actually written.
 *
 * `en-IN` is the point: ₹16,00,00,000 is the same number as ₹160,000,000 and
 * only one of them is readable to an Indian accountant. Every other currency
 * in these books groups in thousands, so `en-US` covers them. Zoho's own
 * `currency_format` sample says the same thing, but as a string to look at
 * rather than one to format with.
 *
 * The decimal places are left to the currency: JPY has none, and forcing two
 * onto it would invent a precision the yen does not have.
 */
export function formatBalance(value: string | number | null, currency: string | null): string {
  const amount = typeof value === "number" ? value : Number.parseFloat(value ?? "");
  if (Number.isNaN(amount)) return "—";

  const code = currency ?? "INR";
  try {
    return new Intl.NumberFormat(code === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    // Intl throws on anything that is not a well-formed ISO 4217 code. A
    // currency we cannot name is not worth taking the page down for; show the
    // number and let the code sit beside it.
    return `${amount.toLocaleString("en-IN")} ${code}`;
  }
}
